import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';

import LoadingSpinner from '../../components/LoadingSpinner';
import { useAxios, APIError } from '../../core/_axios';
import useIpadDeviceChannel from '../../hooks/useIpadDeviceChannel';
import ModalSendServiceToIpad from '../../components/ModalSendServiceToIpad';
import ModalEndServiceOnIpad from '../../components/ModalEndServiceOnIpad';
import { useAuth } from '../../AuthContext';
import { getIPads } from '../../core/_requests_v2';
import { IpadFast } from '../../core/_models_fast';
import useLocalizedCopy from '../../hooks/useLocalizedCopy';

function IpadRow({ iPad: defaultIpad }: { iPad: IpadFast }) {
    const { copy } = useLocalizedCopy();

    const {
        currentUser
    } = useAuth();

    const {
        ipadIsConnected,
        channel
    } = useIpadDeviceChannel({ iPad: defaultIpad });

    const [confirmingLogout, setConfirmingLogout] = useState(false);
    const [sendingServiceModalActive, setSendingServiceModalActive] = useState(false);
    const [endingServiceModalActive, setEndingServiceModalActive] = useState(false);

    const [iPad, setIpad] = useState(defaultIpad);

    const sendLogout = useCallback(() => {
        if (!channel) {
            return;
        }
        channel.push('logout_from_web', {})
            .receive('ok', () => {
                console.log('logout sent')
            })
            .receive('error', () => {
                console.error('logout failed')
            })
    }, [channel]);

    return <>
        <tr>
            <td>
                <div className="flex flex-col gap-1.5">
                    <div className="flex flex-row items-center gap-2">
                        {ipadIsConnected ?
                            <span className="flex w-3 h-3 bg-green-500 rounded-full"></span> :
                            <span className="flex w-3 h-3 bg-gray-300 rounded-full"></span>
                        }
                        <Link
                            to={`/ipads/${iPad.unique_identifier}`}
                            className="leading-none font-semibold text-sm text-gray-900 hover:text-primary"
                        >
                            {iPad.public_data.ipad_name}
                        </Link>
                    </div>
                    <span className="text-2sm text-gray-600">

                    </span>
                </div>
            </td>
            <td>
                {DateTime.fromISO(iPad.inserted_at).toRelative()}
            </td>
            <td>
                {iPad.active_service ? <>
                    <Link
                        className="leading-none font-semibold text-sm text-gray-900 hover:text-primary"
                        to={`/services/${iPad.active_service.unique_identifier}`}
                    >
                        {iPad.active_service.public_data.deceased_person.full_name}
                    </Link>
                </> : copy.IPads.manage.noCurrentService}
            </td>
            <td>
                {confirmingLogout ? <></> : <>
                    {!iPad.active_service ? <button
                        className="btn btn-sm btn-primary me-2"
                        onClick={() => setSendingServiceModalActive(true)}
                    >
                        {copy.IPads.manage.sendService}
                    </button> : <button
                        className="btn btn-sm btn-danger me-2"
                        onClick={() => setEndingServiceModalActive(true)}
                    >
                        {copy.IPads.manage.endService}
                    </button>}
                    {ipadIsConnected && <button
                        className="btn btn-sm btn-light"
                        onClick={() => setConfirmingLogout(true)}
                    >
                        {copy.Common.navigation.logout}
                    </button>}
                </>}
                {confirmingLogout && <>
                    <button
                        className="btn btn-sm btn-light me-2"
                        onClick={() => setConfirmingLogout(false)}
                    >
                        {copy.Common.buttons.cancel}
                    </button>
                    <button
                        className="btn btn-sm btn-danger"
                        onClick={() => {
                            sendLogout()
                            setConfirmingLogout(false)
                        }}
                    >
                        {copy.Common.buttons.confirm}
                    </button>
                </>}
            </td>
        </tr>
        <ModalSendServiceToIpad
            active={sendingServiceModalActive}
            setActive={setSendingServiceModalActive}
            iPad={iPad}
            setIpad={setIpad}
        />
        <ModalEndServiceOnIpad
            active={endingServiceModalActive}
            setActive={setEndingServiceModalActive}
            iPad={iPad}
            setIpad={setIpad}
        />
    </>
}

export function IPadsList() {
    const axios = useAxios();
    const { copy } = useLocalizedCopy();

    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<string | null>(null);
    const [iPads, setiPads] = useState<IpadFast[]>([]);

    const loadiPads = useCallback(async () => {
        setLoading(true);
        setLoadingError(null);
        try {
            const {
                ipads: updatediPads
            } = await getIPads(axios);
            setiPads(updatediPads);
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : copy.Common.messages.errorOccurred
            setLoadingError(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [axios, copy.Common.messages.errorOccurred]);

    useEffect(() => {
        loadiPads();
    }, [loadiPads]);

    if (loading) {
        return <LoadingSpinner />;
    }

    if (loadingError) {
        return (
            <>
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">
                        {loadingError}
                    </span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={loadiPads}>
                        {copy.Common.messages.tryAgain}
                    </button>
                </div>
            </>
        );
    }

    return <>
        <div className="grid gap-5 lg:gap-7.5">
            <div className="card card-grid min-w-full">
                <div className="card-header flex-wrap py-3">
                    <div className="flex-1"></div>
                    <Link to="/add-ipad" className="btn btn-sm btn-primary">
                        <i className="ki-filled ki-plus-squared text-sm me-1"></i>
                        {copy.IPads.list.addIPad}
                    </Link>
                </div>
                <div className="card-body">
                    <div data-datatable="true" data-datatable-page-size="10" id="teams_table">
                        <div className="scrollable-x-auto">
                            <table className="table table-fixed table-border" data-datatable-table="true">
                                {iPads.length > 0 && <thead>
                                    <tr>
                                        <th className="w-[350px]">
                                            <span className="sort asc">
                                                <span className="sort-label">
                                                    {copy.IPads.list.name}
                                                </span>
                                            </span>
                                        </th>
                                        <th className="w-[200px]">
                                            <span className="sort">
                                                <span className="sort-label">
                                                    {copy.Common.labels.date}
                                                </span>
                                            </span>
                                        </th>
                                        <th>
                                            {copy.IPads.list.activeService}
                                        </th>
                                        <th className="w-[200px]">
                                            <span className="sort">
                                                <span className="sort-label">
                                                    {copy.IPads.list.actions}
                                                </span>
                                            </span>
                                        </th>
                                    </tr>
                                </thead>}
                                {iPads.length > 0 ? <tbody>
                                    {iPads.map((iPad) => {
                                        return <IpadRow key={iPad.unique_identifier} iPad={iPad} />
                                    })}
                                </tbody> : <tbody>
                                    <tr>
                                        <td colSpan={3} className="text-center">
                                            <div>
                                                {copy.IPads.list.noIPads}
                                            </div>
                                            <div className="mt-3">
                                                <Link to="/add-ipad" className="btn btn-sm btn-light">
                                                    {copy.IPads.list.addIPad}
                                                </Link>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>}
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </>
}